import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FC, useCallback, useMemo } from 'react';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PermissionGuard from 'src/components/guards/PermissionGuard';
import { CanManageOfflineSuppliers } from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useEnterReceipts from 'src/hooks/useEnterReceipts';
import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';
import AttachDocumentWizard from 'src/sections/attach_document/AttachDocumentWizard';
import useGetManagerApi from 'src/services/useGetManagerApi';
import { IOfflineSupplier } from 'src/types/OfflineSupplier';
import OfflineSupplierGrid from '../OfflineSupplierGrid';

interface CreateReceiptProps {
  openModal: boolean;
  closeModal: () => void;
}

const CreateReceipt: FC<CreateReceiptProps> = ({ openModal, closeModal }) => {
  const { redirect, getUrl } = useRedirect();
  const { managers } = useGetManagerApi();
  const { value: openAttachModal, toggle: toggleAttachModal } = useBoolean(false);
  const canManageOfflineSuppliers = usePermission({ permissions: CanManageOfflineSuppliers });

  const { selectedSupplierReceiptRow, setSelectedReceiptSupplierRow } = useEnterReceipts();
  const isItemSelected = Object.keys(selectedSupplierReceiptRow);
  const handleSelectedSupplier = useCallback(
    (data: IOfflineSupplier) => {
      setSelectedReceiptSupplierRow(data);
    },
    [setSelectedReceiptSupplierRow]
  );

  const handleCreateReceipt = useCallback(() => {
    closeModal();
    toggleAttachModal();
  }, [closeModal, toggleAttachModal]);

  const handleNewSupplier = useCallback(() => {
    redirect(getUrl('manageSupplier'), false, true);
    closeModal();
  }, [closeModal, getUrl, redirect]);

  const managerName = useMemo(
    () => (managers ? managers[0]?.contact.split('-')?.[0] || 'Manager' : 'Manager'),
    [managers]
  );

  return (
    <div>
      <Dialog
        width="80%"
        title="Create New Receipt"
        open={openModal}
        onClose={closeModal}
        id="modal"
      >
        <div>
          <p className="text-sm font-bold mb-2">
            Choose a supplier for this Receipt.{' '}
            {canManageOfflineSuppliers
              ? 'If you cannot find your supplier, create a new one.'
              : `If you cannot find your supplier, ask ${managerName}, or "Customer Support" to add the supplier. Once added, return to this screen to continue.`}
          </p>

          <div className="w-full flex justify-center gap-2 mb-1">
            <Button
              disabled={isItemSelected.length === 0}
              color="primary"
              onClick={handleCreateReceipt}
            >
              Create Receipt
            </Button>
            <PermissionGuard permissions={CanManageOfflineSuppliers}>
              <Button
                color="secondary"
                disabled={isItemSelected.length > 0}
                onClick={handleNewSupplier}
              >
                Create New Supplier
              </Button>
            </PermissionGuard>
          </div>
        </div>
        <OfflineSupplierGrid setSelectedSupplier={handleSelectedSupplier} />
        <DialogActionsBar>
          <div className="flex items-center justify-end gap-2">
            <Button color="info" onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>

      <AttachDocumentWizard
        openModal={openAttachModal}
        closeModal={toggleAttachModal}
        title="Attach to New Receipt"
        isNewDocument={true}
      />
    </div>
  );
};

export default CreateReceipt;
